import { useState } from "react";
import { App, Button, Form, Input, Popconfirm, Select, Space, Table, Typography } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ChannelProduct } from "@glow-price/shared";
import { ENTITY_STATUSES } from "@glow-price/shared";

import { channelProductsApi, channelsApi, standardProductsApi } from "../api/resources";
import { CrudDrawer } from "../components/CrudDrawer";
import { ImageUpload } from "../components/ImageUpload";

const STATUS_OPTIONS = Object.values(ENTITY_STATUSES).map((v) => ({ value: v, label: v }));

export function ChannelProductsPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [standardProductId, setStandardProductId] = useState<string | undefined>();
  const [channelId, setChannelId] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<ChannelProduct | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const productsQuery = useQuery({
    queryKey: ["standard-products", "all"],
    queryFn: () => standardProductsApi.list({ page: 1, pageSize: 500 }),
  });
  const productOptions = (productsQuery.data?.items ?? []).map((r) => ({
    value: r.product.id,
    label: `${r.brand.nameCn} · ${r.product.name}`,
  }));

  const channelsQuery = useQuery({
    queryKey: ["channels", "all"],
    queryFn: () => channelsApi.list({ page: 1, pageSize: 200 }),
  });
  const channelOptions = (channelsQuery.data?.items ?? []).map((c) => ({
    value: c.id,
    label: `${c.name} (${c.code})`,
  }));

  const list = useQuery({
    queryKey: ["channel-products", standardProductId, channelId, page],
    queryFn: () =>
      channelProductsApi.list({ standardProductId, channelId, page, pageSize: 20 }),
  });

  const saveMutation = useMutation({
    mutationFn: async (values: Partial<ChannelProduct>) => {
      if (editing) return channelProductsApi.update(editing.id, values);
      return channelProductsApi.create(values);
    },
    onSuccess: () => {
      message.success("已保存");
      qc.invalidateQueries({ queryKey: ["channel-products"] });
      setDrawerOpen(false);
      setEditing(null);
    },
    onError: (err: Error) => message.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => channelProductsApi.remove(id),
    onSuccess: () => {
      message.success("已删除");
      qc.invalidateQueries({ queryKey: ["channel-products"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  return (
    <div>
      <Space style={{ marginBottom: 16, justifyContent: "space-between", width: "100%" }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          渠道商品管理
        </Typography.Title>
        <Space>
          <Select
            allowClear
            placeholder="按标准商品"
            options={productOptions}
            style={{ width: 280 }}
            value={standardProductId}
            onChange={(v) => {
              setPage(1);
              setStandardProductId(v);
            }}
            showSearch
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
            }
          />
          <Select
            allowClear
            placeholder="按渠道"
            options={channelOptions}
            style={{ width: 180 }}
            value={channelId}
            onChange={(v) => {
              setPage(1);
              setChannelId(v);
            }}
          />
          <Button
            type="primary"
            onClick={() => {
              setEditing(null);
              setDrawerOpen(true);
            }}
          >
            新建渠道商品
          </Button>
        </Space>
      </Space>

      <Table<ChannelProduct>
        rowKey="id"
        loading={list.isLoading}
        dataSource={list.data?.items ?? []}
        pagination={{
          current: page,
          pageSize: 20,
          total: list.data?.total ?? 0,
          onChange: (p) => setPage(p),
          showSizeChanger: false,
        }}
        columns={[
          {
            title: "标准商品",
            dataIndex: "standardProductId",
            render: (v) => productOptions.find((o) => o.value === v)?.label ?? v,
          },
          {
            title: "渠道",
            dataIndex: "channelId",
            width: 160,
            render: (v) => channelOptions.find((o) => o.value === v)?.label ?? v,
          },
          { title: "渠道商品 ID", dataIndex: "channelProductId", width: 180 },
          { title: "渠道商品名", dataIndex: "channelProductName" },
          { title: "path", dataIndex: "miniProgramPath", ellipsis: true },
          {
            title: "操作",
            width: 160,
            render: (_, row) => (
              <Space>
                <Button
                  type="link"
                  onClick={() => {
                    setEditing(row);
                    setDrawerOpen(true);
                  }}
                >
                  编辑
                </Button>
                <Popconfirm title="确认删除？" onConfirm={() => deleteMutation.mutate(row.id)}>
                  <Button type="link" danger>
                    删除
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <CrudDrawer<Partial<ChannelProduct>>
        open={drawerOpen}
        title={editing ? "编辑渠道商品" : "新建渠道商品"}
        initialValues={editing ?? { status: ENTITY_STATUSES.ACTIVE }}
        loading={saveMutation.isPending}
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
        onSubmit={(values) => saveMutation.mutateAsync(values)}
      >
        {() => (
          <>
            <Form.Item name="standardProductId" label="标准商品" rules={[{ required: true }]}>
              <Select
                options={productOptions}
                showSearch
                filterOption={(input, option) =>
                  (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item name="channelId" label="渠道" rules={[{ required: true }]}>
              <Select options={channelOptions} />
            </Form.Item>
            <Form.Item name="channelProductId" label="渠道商品 ID" rules={[{ required: true }]}>
              <Input placeholder="渠道方使用的商品标识" />
            </Form.Item>
            <Form.Item name="channelProductName" label="渠道商品名" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="spec" label="规格">
              <Input />
            </Form.Item>
            <Form.Item name="imageUrl" label="商品图">
              <ImageUpload folder="channel-products" />
            </Form.Item>
            <Form.Item name="productUrl" label="H5 URL">
              <Input />
            </Form.Item>
            <Form.Item name="miniProgramPath" label="小程序 path">
              <Input placeholder="/pages/product/detail?id=..." />
            </Form.Item>
            <Form.Item name="status" label="状态">
              <Select options={STATUS_OPTIONS} />
            </Form.Item>
          </>
        )}
      </CrudDrawer>
    </div>
  );
}
